// src/components/pages/MyContactsPage.js
import React, { useState, useEffect, useMemo } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { auth, db } from '../../firebase'
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  deleteDoc,
  limit,
  onSnapshot,
  documentId,
} from 'firebase/firestore'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import GroupRoundedIcon from '@mui/icons-material/GroupRounded'
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded'
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded'
import Avatar from '@mui/material/Avatar'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { normalizeToken } from '../../utils/searchTokens'
import useAppConfigFlags from '../../hooks/useAppConfigFlags'
import {
  acceptConnectionRequest,
  declineConnectionRequest,
  fetchIncomingConnectionRequests,
  fetchOutgoingConnectionRequests,
  sendConnectionRequest,
} from '../../services/profileService'
import {
  getProfilePathByUid,
  getPublicHandle,
  getPublicProfilePathByHandle,
  isEmailStyleUserName,
  normalizePublicHandle,
} from '../../utils/publicProfile'
import { ellipsizeProfileText } from '../../utils/profileText'
import { buildAvatarImageAttrs } from '../../utils/imageUrl'
import { filterResolvableConnections } from '../../utils/connectionIdentity'
import { APP_SHELL_LAYOUT } from '../../constants/constants'

const BATCH_SIZE = 10
// The bottom nav is fixed and sits on top of the page, so a long friends list ends up
// running underneath it. Clear the nav's full height plus the device's bottom safe area,
// keeping the page's own 16px of breathing room.
const PAGE_BOTTOM_PADDING_CSS = `calc(${APP_SHELL_LAYOUT.BOTTOM_NAV_OFFSET_CSS} + 16px)`
const PAGE_HEADER_SX = {
  fontWeight: 700,
  letterSpacing: 0,
  lineHeight: 1.08,
}

function chunk(arr, size) {
  const chunks = []
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size))
  }
  return chunks
}

function resolveProfileImage(profile) {
  return String(
    profile?.profileImage ||
    profile?.photoURL ||
    profile?.photoUrl ||
    profile?.avatarUrl ||
    ''
  ).trim()
}

function renderAvatarFallback(...values) {
  const initial = values
    .map((value) => String(value || '').trim())
    .find(Boolean)
    ?.charAt(0)

  if (initial) {
    return initial.toUpperCase()
  }

  return <PersonRoundedIcon fontSize="small" />
}

function getFallbackAvatarSx(hasImage) {
  if (hasImage) {
    return {}
  }

  return {
    bgcolor: (theme) =>
      theme.palette.mode === 'dark' ? 'rgba(92, 122, 255, 0.28)' : '#dbe5ff',
    color: (theme) =>
      theme.palette.mode === 'dark' ? '#f4f7ff' : '#2341a1',
  }
}

function getDisplayText(value) {
  const text = String(value || '').trim()
  if (!text) return ''

  const displayText = isEmailStyleUserName(text) ? text.split('@')[0] : text
  return ellipsizeProfileText(displayText)
}

function getProfileDisplay(row, fallback = 'Friend') {
  const handle = normalizePublicHandle(row?.publicHandle || '') || getPublicHandle(row?.userName)
  const displayName =
    getDisplayText(row?.displayName) ||
    (handle ? `@${handle}` : getDisplayText(row?.userName) || fallback)
  const secondary = handle ? `@${handle}` : (row?.phoneNumber || getDisplayText(row?.email) || '')
  const profilePath = getPublicProfilePathByHandle(handle) || getProfilePathByUid(row?.uid)

  return { displayName, handle, profilePath, secondary }
}

function pluralLabel(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`
}

function profileMatchesQuery(row, normalizedQuery) {
  if (!normalizedQuery) return true

  const values = [
    row?.displayName,
    row?.publicHandle,
    row?.userName,
    row?.phoneNumber,
    row?.email,
  ]

  return values.some((value) => normalizeToken(value || '').includes(normalizedQuery))
}

function PersonSummary({ row, fallback = 'Friend', avatarSize = 40 }) {
  const { displayName, handle, secondary, profilePath } = getProfileDisplay(row, fallback)
  const avatarAttrs = buildAvatarImageAttrs(row?.profileImage, { size: avatarSize })

  return (
    <ListItemButton
      component={RouterLink}
      to={profilePath}
      sx={{
        minWidth: 0,
        flex: 1,
        py: 0.5,
        px: 0.5,
        borderRadius: 1,
      }}
    >
      <ListItemAvatar sx={{ minWidth: avatarSize + 8 }}>
        <Avatar
          src={avatarAttrs.src || undefined}
          imgProps={{
            srcSet: avatarAttrs.srcSet || undefined,
            sizes: avatarAttrs.sizes || undefined,
            loading: 'lazy',
            decoding: 'async',
          }}
          sx={{ width: avatarSize, height: avatarSize, ...getFallbackAvatarSx(Boolean(row?.profileImage)) }}
        >
          {renderAvatarFallback(row?.displayName, handle, row?.userName)}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={displayName}
        secondary={secondary || null}
        primaryTypographyProps={{
          fontWeight: 800,
          sx: {
            whiteSpace: 'normal',
            overflow: 'visible',
            textOverflow: 'clip',
            wordBreak: 'break-word',
          },
        }}
        secondaryTypographyProps={{
          sx: {
            color: 'text.secondary',
            whiteSpace: 'normal',
            overflow: 'visible',
            textOverflow: 'clip',
            wordBreak: 'break-word',
          },
        }}
      />
    </ListItemButton>
  )
}

function StatTile({ value, label, tone = 'default', to }) {
  const interactive = Boolean(to)

  return (
    <Box
      {...(interactive
        ? { component: RouterLink, to, 'aria-label': `${value} ${label}, review friend requests` }
        : {})}
      sx={(theme) => ({
        minWidth: 0,
        p: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor:
          tone === 'active'
            ? alpha(theme.palette.success.main, 0.5)
            : theme.palette.divider,
        backgroundColor:
          tone === 'active'
            ? alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.14 : 0.1)
            : theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.04)'
              : 'rgba(255,255,255,0.82)',
        ...(interactive && {
          display: 'block',
          color: 'inherit',
          textDecoration: 'none',
          cursor: 'pointer',
          '&:hover': {
            borderColor:
              tone === 'active'
                ? alpha(theme.palette.success.main, 0.8)
                : theme.palette.text.disabled,
          },
        }),
      })}
    >
      <Typography variant="body2" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
        <Box component="span" sx={{ fontSize: '1.15rem' }}>
          {value}
        </Box>{' '}
        {label}
      </Typography>
    </Box>
  )
}

export default function MyContactsPage() {
  const [contacts, setContacts] = useState([])
  const [incomingRequests, setIncomingRequests] = useState([])
  const [outgoingRequests, setOutgoingRequests] = useState([])
  const [queryText, setQueryText] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [snackbar, setSnackbar] = useState({ open: false, message: '', canUndo: false })
  const [pendingDisconnectUid, setPendingDisconnectUid] = useState('')
  const [activeFriendActionsUid, setActiveFriendActionsUid] = useState('')
  const [lastRemovedConnection, setLastRemovedConnection] = useState(null)
  const [loading, setLoading] = useState(true)
  const uid = auth.currentUser?.uid
  const { userSearchEnabled } = useAppConfigFlags()

  useEffect(() => {
    let alive = true

    async function fetchContacts({ showLoading = false } = {}) {
      if (!uid) return
      if (showLoading) setLoading(true)
      try {
        const subSnap = await getDocs(collection(db, 'users', uid, 'connections'))
        const subIds = subSnap.docs.map(d => d.id)

        const userSnap = await getDoc(doc(db, 'users', uid))
        const arrIds = userSnap.data()?.connections || []

        const allIds = Array.from(new Set([...subIds, ...arrIds]))
        if (!allIds.length) {
          if (alive) setContacts([])
          return
        }

        const batches = chunk(allIds, BATCH_SIZE)
        const profiles = []
        await Promise.all(batches.map(async batch => {
          const q = query(collection(db, 'users'), where(documentId(), 'in', batch))
          const snap = await getDocs(q)
          profiles.push(...snap.docs.map((d) => {
            const data = d.data() || {}
            return {
              ...data,
              uid: d.id,
              profileImage: resolveProfileImage(data),
            }
          }))
        }))

        if (alive) setContacts(filterResolvableConnections(profiles))
      } catch (err) {
        console.error('could not load friends', err)
      } finally {
        if (alive) setLoading(false)
      }
    }

    fetchContacts({ showLoading: true })

    const userRef = doc(db, 'users', uid)
    const connectionsRef = collection(db, 'users', uid, 'connections')

    const unsubUser = onSnapshot(
      userRef,
      () => {
        fetchContacts()
      },
      (err) => {
        console.error('could not subscribe to user doc for friends', err)
      }
    )

    const unsubConnections = onSnapshot(
      connectionsRef,
      () => {
        fetchContacts()
      },
      (err) => {
        console.error('could not subscribe to friends subcollection', err)
      }
    )

    return () => {
      alive = false
      unsubUser()
      unsubConnections()
    }
  }, [uid])

  useEffect(() => {
    if (!uid) return undefined

    const incomingRef = collection(db, 'users', uid, 'connectionRequests')
    const outgoingRef = collection(db, 'users', uid, 'sentConnectionRequests')

    const unsubIncoming = onSnapshot(
      incomingRef,
      async () => {
        try {
          const incoming = await fetchIncomingConnectionRequests(uid)
          setIncomingRequests(incoming)
        } catch (err) {
          console.error('could not load incoming friend requests', err)
        }
      },
      (err) => {
        console.error('could not subscribe to incoming friend requests', err)
      }
    )

    const unsubOutgoing = onSnapshot(
      outgoingRef,
      async () => {
        try {
          const outgoing = await fetchOutgoingConnectionRequests(uid)
          setOutgoingRequests(outgoing)
        } catch (err) {
          console.error('could not load outgoing friend requests', err)
        }
      },
      (err) => {
        console.error('could not subscribe to outgoing friend requests', err)
      }
    )

    return () => {
      unsubIncoming()
      unsubOutgoing()
    }
  }, [uid])

  useEffect(() => {
    let active = true
    const run = async () => {
      if (!userSearchEnabled) {
        if (active) setSearchResults([])
        return
      }

      const token = normalizeToken(queryText).split(' ')[0] || ''
      if (!token || token.length < 2) {
        if (active) setSearchResults([])
        return
      }
      try {
        const q = query(
          collection(db, 'users'),
          where('searchTokens', 'array-contains', token),
          limit(10)
        )
        const snap = await getDocs(q)
        const rows = snap.docs
          .map((d) => {
            const data = d.data() || {}
            return {
              uid: d.id,
              ...data,
              profileImage: resolveProfileImage(data),
            }
          })
          .filter((u) => u.uid !== uid && u.isDiscoverable !== false)
        if (active) setSearchResults(rows)
      } catch (err) {
        console.error('search failed', err)
      }
    }
    run()
    return () => {
      active = false
    }
  }, [queryText, uid, userSearchEnabled])

  const connectedUidSet = useMemo(
    () => new Set(contacts.map((contact) => contact.uid)),
    [contacts]
  )
  const outgoingRequestUidSet = useMemo(
    () => new Set(outgoingRequests.map((request) => request.uid)),
    [outgoingRequests]
  )
  const incomingRequestUidSet = useMemo(
    () => new Set(incomingRequests.map((request) => request.uid)),
    [incomingRequests]
  )
  const normalizedQuery = normalizeToken(queryText)
  const filteredFriends = useMemo(
    () => contacts.filter((contact) => profileMatchesQuery(contact, normalizedQuery)),
    [contacts, normalizedQuery]
  )
  const availableSearchResults = useMemo(
    () =>
      searchResults.filter(
        (row) =>
          !connectedUidSet.has(row.uid) &&
          !outgoingRequestUidSet.has(row.uid) &&
          !incomingRequestUidSet.has(row.uid)
      ),
    [connectedUidSet, incomingRequestUidSet, outgoingRequestUidSet, searchResults]
  )
  const visibleIncomingRequests = incomingRequests.slice(0, 2)
  const shouldShowPeopleToAdd =
    userSearchEnabled &&
    normalizedQuery.length >= 2 &&
    filteredFriends.length === 0 &&
    availableSearchResults.length > 0

  const showSnackbar = (message, canUndo = false) => {
    setSnackbar({ open: true, message, canUndo })
  }

  const requestConnection = async (targetUser) => {
    try {
      setPendingDisconnectUid('')
      setActiveFriendActionsUid('')
      const result = await sendConnectionRequest(auth.currentUser, targetUser)

      if (result?.autoAccepted) {
        setOutgoingRequests((prev) => prev.filter((request) => request.uid !== targetUser.uid))
        setIncomingRequests((prev) => prev.filter((request) => request.uid !== targetUser.uid))
        setContacts((prev) => {
          if (prev.some((contact) => contact.uid === targetUser.uid)) return prev
          return [...prev, targetUser]
        })
        setLastRemovedConnection(null)
        showSnackbar('Friend added.')
        return
      }

      setOutgoingRequests((prev) => {
        if (prev.some((request) => request.uid === targetUser.uid)) return prev
        return [...prev, targetUser]
      })
      showSnackbar('Friend request sent.')
    } catch (err) {
      console.error('failed to send friend request', err)
      showSnackbar('Failed to send friend request.')
    }
  }

  const handleAcceptIncomingRequest = async (request) => {
    if (!uid || !request?.uid) return

    try {
      await acceptConnectionRequest(uid, request.uid)
      setIncomingRequests((prev) => prev.filter((row) => row.uid !== request.uid))
      setOutgoingRequests((prev) => prev.filter((row) => row.uid !== request.uid))
      setContacts((prev) => {
        if (prev.some((contact) => contact.uid === request.uid)) return prev
        return [...prev, request]
      })
      showSnackbar('Friend request accepted.')
    } catch (err) {
      console.error('failed to accept friend request', err)
      showSnackbar('Failed to accept friend request.')
    }
  }

  const handleDeclineIncomingRequest = async (request) => {
    if (!uid || !request?.uid) return

    try {
      await declineConnectionRequest(uid, request.uid)
      setIncomingRequests((prev) => prev.filter((row) => row.uid !== request.uid))
      showSnackbar('Friend request declined.')
    } catch (err) {
      console.error('failed to decline friend request', err)
      showSnackbar('Failed to decline friend request.')
    }
  }

  const removeConnection = async (targetUid) => {
    const removedContact = contacts.find((contact) => contact.uid === targetUid) || null
    const removedIndex = contacts.findIndex((contact) => contact.uid === targetUid)

    try {
      await deleteDoc(doc(db, 'users', uid, 'connections', targetUid))
      await deleteDoc(doc(db, 'users', targetUid, 'connections', uid))
      await updateDoc(doc(db, 'users', uid), { connections: arrayRemove(targetUid) })
      await updateDoc(doc(db, 'users', targetUid), { connections: arrayRemove(uid) })
      setContacts((prev) => prev.filter((contact) => contact.uid !== targetUid))
      setPendingDisconnectUid('')
      setActiveFriendActionsUid('')
      setLastRemovedConnection(
        removedContact
          ? {
              contact: removedContact,
              index: removedIndex,
            }
          : null
      )
      showSnackbar('Friend removed.', Boolean(removedContact))
    } catch (err) {
      console.error('failed to remove friend', err)
      showSnackbar('Failed to remove friend.')
    }
  }

  const handleDisconnectClick = (targetUid) => {
    if (pendingDisconnectUid === targetUid) {
      removeConnection(targetUid)
      return
    }

    setPendingDisconnectUid(targetUid)
  }

  const handleUndoRemove = async () => {
    if (!lastRemovedConnection?.contact?.uid) return

    const restoreTarget = lastRemovedConnection

    try {
      const now = new Date()
      await setDoc(doc(db, 'users', uid, 'connections', restoreTarget.contact.uid), { connectedAt: now }, { merge: true })
      await setDoc(doc(db, 'users', restoreTarget.contact.uid, 'connections', uid), { connectedAt: now }, { merge: true })
      await updateDoc(doc(db, 'users', uid), { connections: arrayUnion(restoreTarget.contact.uid) })
      await updateDoc(doc(db, 'users', restoreTarget.contact.uid), { connections: arrayUnion(uid) })
      setContacts((prev) => {
        if (prev.some((contact) => contact.uid === restoreTarget.contact.uid)) return prev

        const next = [...prev]
        const normalizedIndex = Math.max(0, Math.min(restoreTarget.index, next.length))
        next.splice(normalizedIndex, 0, restoreTarget.contact)
        return next
      })
      setLastRemovedConnection(null)
      showSnackbar('Friend restored.')
    } catch (err) {
      console.error('failed to restore friend', err)
      showSnackbar('Failed to restore friend.')
    }
  }

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false, canUndo: false }))
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '40vh'
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2, pb: PAGE_BOTTOM_PADDING_CSS }}>
      <Box
        sx={{
          maxWidth: 720,
          mx: 'auto',
        }}
      >
        <Box
          sx={{
            p: 2,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'linear-gradient(180deg, rgba(29,25,62,0.96), rgba(11,10,29,0.98))'
                : 'linear-gradient(180deg, rgba(247,248,255,0.98), rgba(240,244,255,0.98))',
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? '0 16px 36px rgba(0,0,0,0.24)'
                : '0 16px 36px rgba(38,19,74,0.08)',
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            sx={{ ...PAGE_HEADER_SX, textAlign: 'left' }}
          >
            Friends
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.35 }}>
            Your private sharing circle in Inssa.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 0.8,
              mt: 1.5,
            }}
          >
            <StatTile value={contacts.length} label="friends" />
            <StatTile
              value={incomingRequests.length}
              label="requests"
              tone={incomingRequests.length ? 'active' : 'default'}
              to="/profile/connections/requests"
            />
            <StatTile value={outgoingRequests.length} label="pending" to="/profile/connections/requests" />
          </Box>
        </Box>

        <Box sx={{ mt: 1.5 }}>
          <TextField
            fullWidth
            placeholder="Search friends or people"
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {incomingRequests.length > 0 ? (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="space-between" sx={{ mb: 0.75 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <MailOutlineRoundedIcon fontSize="small" sx={{ color: (theme) => theme.palette.success.main }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  Friend Requests
                </Typography>
              </Stack>
              <Button
                component={RouterLink}
                to="/profile/connections/requests"
                size="small"
                sx={{ textTransform: 'none', fontWeight: 800 }}
              >
                Review all
              </Button>
            </Stack>
            <List sx={{ p: 0 }}>
              {visibleIncomingRequests.map((request) => {
                const { displayName } = getProfileDisplay(request, 'Friend')
                return (
                  <ListItem key={request.uid} disablePadding sx={{ mb: 0.8 }}>
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        p: 0.625,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: (theme) => alpha(theme.palette.success.main, 0.42),
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'dark'
                            ? alpha(theme.palette.success.main, 0.08)
                            : alpha(theme.palette.success.main, 0.06),
                      }}
                    >
                      <PersonSummary row={request} fallback="Friend" />
                      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                        <IconButton
                          aria-label={`Decline ${displayName}`}
                          size="small"
                          onClick={() => handleDeclineIncomingRequest(request)}
                          sx={{ border: '1px solid', borderColor: 'divider' }}
                        >
                          <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          aria-label={`Accept ${displayName}`}
                          size="small"
                          onClick={() => handleAcceptIncomingRequest(request)}
                          sx={(theme) => ({
                            border: '1px solid',
                            borderColor: alpha(theme.palette.success.main, 0.55),
                            color: theme.palette.success.main,
                          })}
                        >
                          <CheckRoundedIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Box>
                  </ListItem>
                )
              })}
            </List>
          </Box>
        ) : null}

        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="space-between" sx={{ mb: 0.75 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <GroupRoundedIcon
                fontSize="small"
                sx={{
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.main,
                }}
              />
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                Friends
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>
              {pluralLabel(filteredFriends.length, 'friend')}
            </Typography>
          </Stack>

          {contacts.length === 0 ? (
            <Box
              sx={{
                p: 2.5,
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'divider',
                textAlign: 'center',
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(250,251,255,0.9)',
              }}
            >
              <GroupRoundedIcon sx={{ color: 'text.secondary', mb: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
                No friends yet
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Search above to start building your private sharing circle.
              </Typography>
            </Box>
          ) : filteredFriends.length === 0 ? (
            <Alert severity="info">No friends match "{queryText.trim()}".</Alert>
          ) : (
            <List sx={{ p: 0 }}>
              {filteredFriends.map((friend) => {
                const { displayName } = getProfileDisplay(friend, 'Friend')
                const isActionsOpen = activeFriendActionsUid === friend.uid
                const isConfirming = pendingDisconnectUid === friend.uid

                return (
                  <ListItem
                    key={friend.uid}
                    disablePadding
                    sx={{ mb: 0.9 }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        p: 0.625,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.92)',
                      }}
                    >
                      <PersonSummary row={friend} fallback="Friend" />
                      {isActionsOpen ? (
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<LinkOffRoundedIcon fontSize="small" />}
                          onClick={() => handleDisconnectClick(friend.uid)}
                          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                        >
                          {isConfirming ? 'Confirm' : 'Remove friend'}
                        </Button>
                      ) : (
                        <IconButton
                          aria-label={`More actions for ${displayName}`}
                          size="small"
                          onClick={() => {
                            setActiveFriendActionsUid(friend.uid)
                            setPendingDisconnectUid('')
                          }}
                          sx={{ border: '1px solid', borderColor: 'divider', flexShrink: 0 }}
                        >
                          <MoreHorizRoundedIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </ListItem>
                )
              })}
            </List>
          )}
        </Box>

        {shouldShowPeopleToAdd ? (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
              <PersonAddAlt1RoundedIcon
                fontSize="small"
                sx={{
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.main,
                }}
              />
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                People you can add
              </Typography>
            </Stack>
            <List sx={{ p: 0 }}>
              {availableSearchResults.map((person) => (
                <ListItem
                  key={`search-${person.uid}`}
                  disablePadding
                  sx={{ mb: 0.75 }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      minWidth: 0,
                      gap: 0.75,
                      p: 0.75,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(248,250,255,0.9)',
                    }}
                  >
                    <PersonSummary row={person} fallback="User" />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PersonAddAlt1RoundedIcon fontSize="small" />}
                      sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                      onClick={() => requestConnection(person)}
                    >
                      Request
                    </Button>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>
        ) : null}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.canUndo ? 5000 : 3000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          action={
            snackbar.canUndo ? (
              <Button color="inherit" size="small" onClick={handleUndoRemove}>
                Undo
              </Button>
            ) : null
          }
          variant="filled"
          severity="info"
          sx={{ width: '100%', '& .MuiAlert-action': { color: '#fff' } }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
